import { useState } from 'react'
import { useParams, Navigate, Link, Outlet } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog'
import { PhaseBadge, PromptSectionsNav } from '@/components/projects'
import { useAuth, usePrompt, useProject, useUnsavedChangesGuard, useUpdatePrompt } from '@/hooks'
import type { PromptDetailContext } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { Permissions, canEditPromptPhase } from '@/lib/permissions'
import { KEBAB_CASE_REGEX } from '@/types'
import { toast } from 'sonner'

const NAME_MAX = 256
const DESCRIPTION_MAX = 2000
const CONTENT_MAX = 50000

export function PromptDetailPage() {
  const { promptId } = useParams()
  return <PromptDetailContent key={promptId} />
}

function PromptDetailContent() {
  const { projectId, promptId } = useParams()
  const { hasPermission, getProjectRoles, isLoading: authLoading } = useAuth(projectId)
  const { data: prompt, isLoading, isFetching } = usePrompt(projectId!, promptId)
  const { data: project } = useProject(projectId)
  const updatePrompt = useUpdatePrompt(projectId!)

  const [nameDraft, setNameDraft] = useState<string | null>(null)
  const [internalNameDraft, setInternalNameDraft] = useState<string | null>(null)
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null)
  const [contentDraft, setContentDraft] = useState<string | null>(null)

  const name = nameDraft ?? prompt?.name ?? ''
  const internalName = internalNameDraft ?? prompt?.internalName ?? ''
  const description = descriptionDraft ?? prompt?.description ?? ''
  const content = contentDraft ?? prompt?.content ?? ''

  const trimmedName = name.trim()
  const trimmedInternalName = internalName.trim()
  const trimmedDescription = description.trim()
  const trimmedContent = content.trim()

  const isDirty =
    !!prompt &&
    (trimmedName !== prompt.name.trim() ||
      trimmedInternalName !== prompt.internalName.trim() ||
      trimmedDescription !== (prompt.description ?? '').trim() ||
      trimmedContent !== prompt.content.trim())
  const isSaving = updatePrompt.isPending

  const validationErrors: PromptDetailContext['validationErrors'] = {}
  if (!trimmedName) validationErrors.name = 'Name is required'
  else if (trimmedName.length > NAME_MAX) validationErrors.name = `Name must not exceed ${NAME_MAX} characters`
  if (!trimmedInternalName) validationErrors.internalName = 'Internal Name is required'
  else if (trimmedInternalName.length > NAME_MAX) validationErrors.internalName = `Internal Name must not exceed ${NAME_MAX} characters`
  else if (!KEBAB_CASE_REGEX.test(trimmedInternalName)) validationErrors.internalName = 'Must be kebab-case (e.g. "my-prompt-1")'
  if (trimmedDescription.length > DESCRIPTION_MAX) validationErrors.description = `Description must not exceed ${DESCRIPTION_MAX} characters`
  if (!trimmedContent) validationErrors.content = 'Content is required'
  else if (trimmedContent.length > CONTENT_MAX) validationErrors.content = `Content must not exceed ${CONTENT_MAX} characters`

  const validationError = Object.values(validationErrors)[0] ?? null
  let saveBlockedReason: string | null = null
  if (isSaving) saveBlockedReason = 'Saving…'
  else if (!isDirty) saveBlockedReason = 'No changes to save.'
  else if (isFetching) saveBlockedReason = 'Reloading the prompt…'
  else if (validationError) saveBlockedReason = validationError

  const promptsPath = `/projects/${projectId}/workflows/prompts`
  const promptPath = `${promptsPath}/${promptId}`

  const guard = useUnsavedChangesGuard({
    isDirty: isDirty || isSaving,
    isNavigationAllowed: next => next.pathname === promptPath || next.pathname.startsWith(`${promptPath}/`),
  })

  const discardDrafts = () => {
    setNameDraft(null)
    setInternalNameDraft(null)
    setDescriptionDraft(null)
    setContentDraft(null)
  }

  const save = async (): Promise<boolean> => {
    if (!prompt || saveBlockedReason) return false
    try {
      await updatePrompt.mutateAsync({
        id: prompt.id,
        data: {
          name: trimmedName,
          internalName: trimmedInternalName,
          content: trimmedContent,
          ...(trimmedDescription ? { description: trimmedDescription } : {}),
        },
      })
      setNameDraft(prev => (prev === name ? null : prev))
      setInternalNameDraft(prev => (prev === internalName ? null : prev))
      setDescriptionDraft(prev => (prev === description ? null : prev))
      setContentDraft(prev => (prev === content ? null : prev))
      toast.success('Prompt updated successfully')
      return true
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to update prompt')
      return false
    }
  }

  if (authLoading) return null

  if (!hasPermission(Permissions.Project.Prompt.Read, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  if (isLoading) {
    return (
      <div className="py-12 text-center text-muted-foreground" role="status" aria-live="polite" data-testid="prompt-detail-loading">
        Loading prompt…
      </div>
    )
  }

  if (!prompt) {
    return (
      <div className="space-y-4 py-12 text-center" role="alert" data-testid="prompt-detail-error">
        <p className="text-sm text-destructive">Couldn't load this prompt. It may have been deleted.</p>
        <Button variant="outline" asChild>
          <Link to={promptsPath}>
            <ArrowLeft className="h-4 w-4" />
            Back to Prompts
          </Link>
        </Button>
      </div>
    )
  }

  const canEdit =
    canEditPromptPhase(prompt.projectPhaseName, getProjectRoles(projectId!)) &&
    hasPermission(Permissions.Project.Prompt.Update, projectId)

  const context: PromptDetailContext = {
    prompt,
    canEdit,
    name,
    onNameChange: setNameDraft,
    internalName,
    onInternalNameChange: setInternalNameDraft,
    description,
    onDescriptionChange: setDescriptionDraft,
    content,
    onContentChange: setContentDraft,
    validationErrors,
    isDirty,
    isSaving,
    saveBlockedReason,
    onSave: () => void save(),
    onCancel: discardDrafts,
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6" data-testid="prompt-detail-page">
      <div className="min-w-0 shrink-0">
        <p className="text-sm font-medium text-primary">
          Project: {project?.name ?? 'Loading...'}
        </p>
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" asChild className="shrink-0" aria-label="Back to Prompts">
            <Link to={promptsPath}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <h1 className="truncate text-2xl font-bold text-foreground">{prompt.name}</h1>
          <PhaseBadge name={prompt.projectPhaseName} color={prompt.projectPhaseDisplayColor} />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        <PromptSectionsNav />
        <div className="min-h-0 min-w-0 flex-1">
          <Outlet context={context} />
        </div>
      </div>

      <UnsavedChangesDialog
        open={guard.isBlocked}
        isSaving={isSaving}
        saveBlockedReason={isSaving ? null : saveBlockedReason}
        onSave={() => void save().then(saved => { if (saved) guard.leave() })}
        onDiscard={guard.leave}
        onStay={guard.stay}
      />
    </div>
  )
}
